import moment from "moment";
import { parseLocalDateString } from "helpers/Helpers";

export type GiftCardExpirySetting = "no_expiry" | "one_year" | "end_of_year" | "custom_years";
export type GiftCardProductExpiryType = GiftCardExpirySetting | "inherit";

export function resolveGiftCardExpiryPreview(
  facilityExpiry: GiftCardExpirySetting,
  facilityExpiryYears: number,
  productExpiryType?: GiftCardProductExpiryType,
  productExpiryYears?: number,
  fromDate: Date = new Date(),
): string | null {
  let expiryType = productExpiryType ?? "inherit";
  let expiryYears = productExpiryYears ?? facilityExpiryYears;

  if (expiryType === "inherit") {
    expiryType = facilityExpiry;
    expiryYears = facilityExpiryYears;
  }

  return computeGiftCardExpiryPreview(expiryType, expiryYears, fromDate);
}

export function computeGiftCardExpiryPreview(
  expiryType: GiftCardProductExpiryType,
  customYears = 5,
  fromDate: Date = new Date(),
): string | null {
  const base = moment(fromDate);

  switch (expiryType) {
    case "one_year":
      return base.clone().add(1, "year").endOf("day").format("MMMM D, YYYY");
    case "end_of_year":
      return base.clone().endOf("year").endOf("day").format("MMMM D, YYYY");
    case "custom_years":
      return base.clone().add(customYears, "years").endOf("day").format("MMMM D, YYYY");
    case "no_expiry":
    case "inherit":
    default:
      return null;
  }
}

export function isGiftCardExpired(
  expiresAt?: string | null,
  status?: string,
  reference: moment.Moment = moment(),
): boolean {
  if (status === "expired") {
    return true;
  }

  if (!expiresAt) {
    return false;
  }

  const parsed = parseLocalDateString(expiresAt);
  const expiry = parsed ? moment(parsed) : moment(expiresAt);

  return expiry.isBefore(reference, "day");
}

export function formatGiftCardExpiryDate(expiresAt?: string | null): string | null {
  if (!expiresAt) {
    return null;
  }

  const parsed = parseLocalDateString(expiresAt);
  if (parsed) {
    return moment(parsed).format("MMMM Do, YYYY");
  }

  return moment(String(expiresAt)).format("MMMM Do, YYYY");
}

export function getDefaultGiftCardPosOverrideExpiry(fromDate: Date = new Date()): Date {
  return moment(fromDate).add(1, "year").endOf("day").toDate();
}

export function serializeGiftCardsForSale<T extends { override_expiry?: boolean; expires_at?: string }>(
  giftCards: T[] | null | undefined,
): Array<Omit<T, "override_expiry">> | null {
  if (!giftCards) {
    return null;
  }

  return giftCards.map(({ override_expiry, expires_at, ...card }) => ({
    ...card,
    ...(override_expiry && expires_at ? { expires_at } : {}),
  }));
}
